import { DateTime } from 'luxon';

import { TColorKeys } from 'theme/palette';

export enum ETrainingAvailabilityStatus {
    Inactive = 'inactive',
    Always = 'always',
    From = 'from',
    Until = 'until',
    Range = 'range',
}

interface ITrainingAvailability {
    status: ETrainingAvailabilityStatus;
    color: TColorKeys;
    startDate?: string;
    endDate?: string;
}

interface IGetTrainingAvailabilityOptions {
    isActive: boolean;
    startDate?: string | null;
    endDate?: string | null;
    referenceDate?: DateTime;
}

// e.g. "07 jan 2025". The `nl` locale may render abbreviated months with a
// trailing dot ("jan."), which we strip to match the requested format.
const formatAvailabilityDate = (dateISO: string): string => {
    const date = DateTime.fromISO(dateISO).setLocale('nl');
    const day = date.toFormat('dd');
    const month = date.toFormat('MMM').replaceAll('.', '');

    return `${day} ${month} ${date.toFormat('yyyy')}`;
};

/**
 * Derive the availability status, indicator color and formatted dates for a
 * training, based on its active state and (deprecated) start/end dates.
 */
export const getTrainingAvailability = ({
    isActive,
    startDate,
    endDate,
    referenceDate = DateTime.now(),
}: IGetTrainingAvailabilityOptions): ITrainingAvailability => {
    if (!isActive) {
        return { status: ETrainingAvailabilityStatus.Inactive, color: 'error' };
    }

    if (!startDate) {
        // With only an end date the training is available up to that date.
        if (endDate) {
            const end = DateTime.fromISO(endDate).endOf('day');
            const isExpired = referenceDate > end;

            return {
                status: ETrainingAvailabilityStatus.Until,
                color: isExpired ? 'error' : 'success',
                endDate: formatAvailabilityDate(endDate),
            };
        }

        // Without any dates the training is always available.
        return { status: ETrainingAvailabilityStatus.Always, color: 'success' };
    }

    const start = DateTime.fromISO(startDate).startOf('day');
    const formattedStart = formatAvailabilityDate(startDate);

    if (!endDate) {
        const hasStarted = referenceDate >= start;

        return {
            status: ETrainingAvailabilityStatus.From,
            color: hasStarted ? 'success' : 'error',
            startDate: formattedStart,
        };
    }

    const end = DateTime.fromISO(endDate).endOf('day');
    const isWithinRange = referenceDate >= start && referenceDate <= end;

    return {
        status: ETrainingAvailabilityStatus.Range,
        color: isWithinRange ? 'success' : 'error',
        startDate: formattedStart,
        endDate: formatAvailabilityDate(endDate),
    };
};
